import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiUsers, FiArrowRight, FiCheckSquare, FiFolder, FiUserPlus, 
  FiDollarSign, FiList, FiCalendar, FiBell, FiStar
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const PersonalWorkspaceDocs = () => {
  const features = [
    {
      icon: FiCheckSquare,
      title: 'Task Management',
      description: 'Create, organize, and track tasks with priorities, due dates, labels, and subtasks.',
      link: '/docs/personal/tasks',
      color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
    },
    {
      icon: FiFolder,
      title: 'Project Management',
      description: 'Organize work into projects with multiple views including Kanban boards and lists.',
      link: '/docs/personal/projects',
      color: 'bg-green-50 dark:bg-green-900/30 text-green-600'
    },
    {
      icon: FiUserPlus,
      title: 'Personal Teams',
      description: 'Create and manage small teams for collaboration without organization overhead.',
      link: '/docs/personal/teams',
      color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600'
    },
    {
      icon: FiList,
      title: 'Todos',
      description: 'Quick personal todo lists and shared todos for lightweight task tracking.',
      link: '/docs/personal/todos',
      color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600'
    },
    {
      icon: FiDollarSign,
      title: 'Personal Finance',
      description: 'Track personal finances with member management and expense tracking.',
      link: '/docs/personal/finance',
      color: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600'
    }
  ];

  const capabilities = [
    {
      title: 'Unlimited Projects',
      description: 'Create as many projects as you need to organize your work.'
    },
    {
      title: 'Multiple Views',
      description: 'Switch between Kanban, List, Calendar, and Table views.'
    },
    {
      title: 'Task Priorities',
      description: 'Mark tasks as Low, Medium, High, or Critical priority.'
    },
    {
      title: 'Due Dates & Reminders',
      description: 'Set deadlines and receive notifications before tasks are due.'
    },
    {
      title: 'Subtasks & Checklists',
      description: 'Break down complex tasks into smaller, actionable items.'
    },
    {
      title: 'Labels & Tags',
      description: 'Categorize tasks with custom labels for easy filtering.'
    },
    {
      title: 'File Attachments',
      description: 'Attach files and documents directly to tasks.'
    },
    {
      title: 'Comments & Activity',
      description: 'Discuss tasks and track all changes in the activity feed.'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/personal" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Personal Workspace</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl mb-4">
              <FiUsers className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Personal Workspace</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Your personal productivity hub for managing tasks, projects, and collaborating 
              with small teams. Perfect for individuals, freelancers, and small groups.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">What is Personal Workspace?</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                The Personal Workspace is designed for individuals and small teams who want 
                powerful task management without the complexity of enterprise hierarchies. 
                It provides all the essential tools you need to stay organized and productive.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FiStar className="text-yellow-500" />
                    <span className="font-medium text-gray-900 dark:text-white">Best For</span>
                  </div>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Individual professionals</li>
                    <li>• Freelancers and consultants</li>
                    <li>• Small teams (2-10 members)</li>
                    <li>• Personal project management</li>
                  </ul>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FiStar className="text-yellow-500" />
                    <span className="font-medium text-gray-900 dark:text-white">Key Benefits</span>
                  </div>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Simple and intuitive interface</li>
                    <li>• Quick setup - start in minutes</li>
                    <li>• Flexible collaboration options</li>
                    <li>• No complex role hierarchies</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Features Grid */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Link
                    key={index}
                    to={feature.link}
                    className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm hover:shadow-md hover:border-blue-200 transition-all"
                  >
                    <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{feature.description}</p>
                    <div className="flex items-center text-blue-600 text-sm font-medium">
                      <span>Learn more</span>
                      <FiArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Capabilities */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Capabilities</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {capabilities.map((capability, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <FiCheckSquare className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{capability.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{capability.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Dashboard Overview */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Dashboard Overview</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Your personal dashboard gives you a quick overview of your work:
            </p>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <FiCheckSquare className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Task Summary</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">View tasks due today, upcoming, and overdue at a glance.</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <FiFolder className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Project Progress</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Track completion rates and recent activity across projects.</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <FiCalendar className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Calendar View</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">See all scheduled tasks and deadlines on a calendar.</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <FiBell className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Notifications</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Stay updated with mentions, assignments, and reminders.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Explore More</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/personal/tasks"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Management Guide</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/projects"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Project Management Guide</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/teams"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Personal Teams Guide</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/organization"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-transparent hover:border-purple-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Upgrade to Organization</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default PersonalWorkspaceDocs;
